import { Search as SearchIcon } from '@mui/icons-material'
import {
	Box,
	FormControl,
	MenuItem,
	OutlinedInput,
	Select,
	SelectChangeEvent,
	Chip,
	TextField,
	InputAdornment,
} from '@mui/material'
import { GridRenderHeaderFilterProps, useGridApiContext } from '@mui/x-data-grid-pro'
import React, { useState, useMemo } from 'react'

import { useFeedbackGridContext } from '../../context/FeedbackGridContext'
import { FilterOption } from '../../types/feedbackGrid.types'

export const MultiSelectHeaderFilter: React.FC<GridRenderHeaderFilterProps> = ({ item, colDef, inputRef }) => {
	const apiRef = useGridApiContext()
	const { translations, loadingOptions, loadFilterOptions } = useFeedbackGridContext()

	const getInitialValues = (val: any): string[] => {
		// If MUI gives us undefined, check the current filter model from API as fallback
		if (val === undefined || val === null || val === '') {
			const currentModel = apiRef.current.state.filter.filterModel
			const currentItem = currentModel.items.find((i) => i.field === colDef.field)
			if (currentItem && currentItem.value !== undefined && currentItem.value !== null && currentItem.value !== '') {
				val = currentItem.value
			} else {
				return []
			}
		}

		if (Array.isArray(val)) {
			// Filter out empty strings/nulls if any
			return val.filter((v) => v !== null && v !== undefined && v !== '').map(String)
		}
		if (val !== undefined && val !== null && val !== '') {
			return [String(val)]
		}
		return []
	}

	const currentValues = getInitialValues(item.value)

	const loading = loadingOptions[colDef.field] || false

	// If options are not available yet (e.g. initial load), try to get them from context
	const { filterOptions } = useFeedbackGridContext()

	const effectiveOptions = useMemo(() => {
		const options = (colDef.valueOptions as FilterOption[]) || []
		return options.length > 0 ? options : filterOptions[colDef.field] || []
	}, [colDef.valueOptions, filterOptions, colDef.field])

	const [searchTerm, setSearchTerm] = useState('')

	const filteredOptions = useMemo(() => {
		if (!searchTerm) {
			return effectiveOptions
		}
		const lowerSearchTerm = searchTerm.toLowerCase()
		return effectiveOptions.filter((option) => option.label.toLowerCase().includes(lowerSearchTerm))
	}, [effectiveOptions, searchTerm])

	return (
		<FormControl fullWidth size="small" variant="standard">
			<Select
				multiple
				value={currentValues}
				onChange={(e: SelectChangeEvent<string[]>) => {
					const value = e.target.value
					const newValue = typeof value === 'string' ? value.split(',') : value
					apiRef.current.upsertFilterItem({
						...item,
						value: newValue,
						operator: 'isAnyOf',
					})
				}}
				onOpen={() => {
					loadFilterOptions(colDef.field)
					setSearchTerm('')
				}}
				onFocus={() => apiRef.current.startHeaderFilterEditMode(colDef.field)}
				onBlur={() => apiRef.current.stopHeaderFilterEditMode()}
				displayEmpty
				renderValue={(selected) => {
					const selectedArray = selected as string[]
					if (selectedArray.length === 0) {
						return <Box sx={{ color: 'text.disabled', py: '3px' }}>{colDef.headerName}</Box>
					}
					return (
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: '3px' }}>
							{selectedArray.map((v) => {
								const option = effectiveOptions.find((opt) => String(opt.value) === String(v))
								return <Chip key={v} label={option?.label || v} size="small" />
							})}
						</Box>
					)
				}}
				input={<OutlinedInput size="small" ref={inputRef} />}
				MenuProps={{
					autoFocus: false, // Prevents focus from jumping to the first item, allowing us to focus the search field
				}}
			>
				<Box
					sx={{
						p: 1,
						position: 'sticky',
						top: 0,
						backgroundColor: 'white',
						zIndex: 1,
					}}
					onKeyDown={(e) => {
						// Prevent Select from processing keydown events while typing in the search field
						e.stopPropagation()
					}}
				>
					<TextField
						fullWidth
						size="small"
						placeholder={translations.search || 'Search...'}
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						autoFocus
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon size="small" />
								</InputAdornment>
							),
						}}
					/>
				</Box>
				{loading && <MenuItem disabled>{translations.loading || 'Loading...'}</MenuItem>}
				{!loading && filteredOptions.length === 0 && searchTerm && (
					<MenuItem disabled>{translations.noResults || 'No results found'}</MenuItem>
				)}
				{!loading &&
					filteredOptions.map((option) => (
						<MenuItem key={option.value} value={String(option.value)}>
							{option.label}
						</MenuItem>
					))}
			</Select>
		</FormControl>
	)
}
